import { useEffect, useState } from "react";
import { Button, Modal } from "semantic-ui-react";
import AddOrgUserForm, { FormData } from "./AddOrgUserForm";
import { FormError } from "../models/FormError";
import { User } from "../models/User";
import { CalibrationCertificate } from "../models/CalibrationCertificate";
import { trancateTime } from "../utils";
import { LicenceType, OrgUserFileInput } from "../models/globalTypes";
import AddOrgUserFileModalContainer from "../containers/AddOrgUserFileModalContainer";
import { ActiveLicence } from "../models/ActiveLicence";
import { AdminRole } from "../models/AdminRole";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    adminRoleId: string | null,
    phoneNumber: string,
    calibrationCertificate: CalibrationCertificate,
    orgUserFiles: OrgUserFileInput[],
    licenceId: string | null,
  ) => void;
  submitInProgress: boolean;
  submitErrors?: string[];
  onEmailEntered: (email: string) => void;
  user?: User;
  activeLicences?: ActiveLicence[] | null;
  canAssignAdminRole: boolean;
  adminRoles?: AdminRole[];
}

const AddOrgUserModal = ({
  user,
  open,
  onClose,
  onSubmit,
  submitInProgress,
  submitErrors,
  onEmailEntered,
  activeLicences,
  canAssignAdminRole,
  adminRoles,
}: Props) => {
  const [formData, setFormData] = useState<FormData>({} as FormData);
  const [formErrors, setFormErrors] = useState<FormError>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [orgUserFiles, setOrgUserFiles] = useState<OrgUserFileInput[]>([]);
  const [openAddOrgUserFileModal, setOpenAddOrgUserFileModal] = useState(false);
  const [isViewerLicenceSelected, setIsViewerLicenceSelected] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({ email: user.email, firstName: "", lastName: "", adminRoleId: null } as FormData);
    } else {
      setFormData({} as FormData);
    }
    setFormErrors({} as FormError);
    setHasSubmitted(false);
  }, [open, user]);

  const validateFormData = (formData: FormData): FormError => {
    const formError: FormError = {};

    const emailPattern = new RegExp(
      /^(?:"[\w\s'-]+"|[\w'-]+(?:\.[\w'-]+)*)@(?:(?:[\w-]+\.)*\w[\w-]{0,66}\.[a-z]{2,6}(?:\.[a-z]{2})?|\[(?:(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\])$/i,
    );

    if (user) {
      return formError;
    }

    if (!formData.firstName) {
      formError.firstName = "Please enter first name";
    }

    if (!formData.lastName) {
      formError.lastName = "Please enter last name";
    }

    if (!formData.password) {
      formError.password = "Please enter password";
    }

    if (!formData.repeatPassword) {
      formError.repeatPassword = "Please enter password again";
    } else if (formData.password !== formData.repeatPassword) {
      formError.repeatPassword = "Passwords don't match";
    }

    if (!formData.email) {
      formError.email = "Please enter email address";
    } else if (!emailPattern.test(formData.email)) {
      formError.email = "Email address format is not correct";
    }

    return formError;
  };

  const handleSubmit = () => {
    setHasSubmitted(true);

    const validationErrors = validateFormData(formData);
    setFormErrors(validationErrors);

    // If there is no validation error
    if (Object.keys(validationErrors).length === 0) {
      const {
        password,
        firstName,
        lastName,
        email,
        adminRoleId,
        phoneNumber,
        CatSerialNumber,
        CatCalibrationDate,
        CatCalibrationDueDate,
        GennySerialNumber,
        GennyCalibrationDate,
        GennyCalibrationDueDate,
        licenceId,
      } = formData;
      onSubmit(
        firstName,
        lastName,
        email,
        password,
        adminRoleId || null,
        phoneNumber,
        {
          catSerialNumber: CatSerialNumber,
          catCalibrationDate: CatCalibrationDate ? trancateTime(CatCalibrationDate) : null,
          catCalibrationDueDate: CatCalibrationDueDate ? trancateTime(CatCalibrationDueDate) : null,
          gennySerialNumber: GennySerialNumber,
          gennyCalibrationDate: GennyCalibrationDate ? trancateTime(GennyCalibrationDate) : null,
          gennyCalibrationDueDate: GennyCalibrationDueDate ? trancateTime(GennyCalibrationDueDate) : null,
        } as CalibrationCertificate,
        orgUserFiles,
        licenceId,
      );
    }
  };

  const handleFormChange = (formData: FormData) => {
    if (hasSubmitted) {
      const validationErrors = validateFormData(formData);
      setFormErrors(validationErrors);
    }

    const viewerLicenceSelected =
      activeLicences?.find((l) => l.id === formData.licenceId)?.type === LicenceType.VIEWER;
    setIsViewerLicenceSelected(viewerLicenceSelected);
    if (viewerLicenceSelected) {
      formData.adminRoleId = null;
    }
    setFormData(formData);
  };

  const handleOrgUserFileAdded = (orgUserFile: OrgUserFileInput) => {
    setOrgUserFiles([...orgUserFiles, orgUserFile]);
    setOpenAddOrgUserFileModal(false);
  };

  const handleDeleteOrgUserFile = (orgUserFile: OrgUserFileInput) => {
    setOrgUserFiles([...orgUserFiles.filter((f) => f !== orgUserFile)]);
  };

  const handelClose = () => {
    setOrgUserFiles([]);
    onClose();
  };

  return (
    <>
      <Modal open={open} onClose={handelClose}>
        <Modal.Content>
          <Modal.Description>
            <AddOrgUserForm
              user={user}
              formErrors={formErrors}
              errors={submitErrors}
              formData={formData}
              onChange={handleFormChange}
              onEmailEntered={onEmailEntered}
              onAddOrgUserFile={() => setOpenAddOrgUserFileModal(true)}
              onDeleteOrgUserFile={handleDeleteOrgUserFile}
              orgUserFiles={orgUserFiles}
              activeLicences={activeLicences}
              adminRoles={adminRoles}
              adminRoleSelectorDisabled={!canAssignAdminRole || isViewerLicenceSelected}
            ></AddOrgUserForm>
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handelClose}>Cancel</Button>
          <Button onClick={handleSubmit} positive loading={submitInProgress} disabled={submitInProgress}>
            Ok
          </Button>
        </Modal.Actions>
      </Modal>

      <AddOrgUserFileModalContainer
        open={openAddOrgUserFileModal}
        onOrgUserFileAdded={handleOrgUserFileAdded}
        onClose={() => setOpenAddOrgUserFileModal(false)}
      ></AddOrgUserFileModalContainer>
    </>
  );
};

export default AddOrgUserModal;
